import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  UserIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import googleMapsLoader from '../utils/googleMapsLoader';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const DEFAULT_LOCATION_NAME = 'Medical Facility';

// UI Components
const Button = ({ children, onClick, variant = 'primary', disabled = false, className = '', type = 'button' }) => {
  const baseClasses = 'px-4 py-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

const Input = ({ value, onChange, placeholder, type = 'text', className = '', label, required = false, disabled = false, readOnly = false }) => (
  <div className={className}>
    {label && (
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    )}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>
);

const ServiceLocationSelector = ({ 
  selectedLocation, 
  onLocationSelect, 
  placeholder = "Select a service location",
  showAddNew = true,
  className = ""
}) => {
  const { makeAuthenticatedRequest } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    address_2: '',
    phone_number: '',
    email_address: '',
    city: '',
    state: '',
    zip_code: '',
    latitude: null,
    longitude: null
  });
  const [submitting, setSubmitting] = useState(false);
  const addressAutocompleteRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    loadLocations();
    initializeGoogleMaps();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  }, [showDropdown]);

  useEffect(() => {
    if (showAddModal && mapsInitialized) {
      setTimeout(() => {
        setupAddressAutocomplete();
      }, 100);
    }
  }, [showAddModal, mapsInitialized]);

  const initializeGoogleMaps = async () => {
    try {
      await googleMapsLoader.load(['places']);
      setMapsInitialized(true);
    } catch (error) {
      console.error('Error loading Google Maps:', error);
      setMapsInitialized(false);
    }
  };

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest('/service-locations');
      
      if (response.success) {
        setLocations(response.data);
      } else {
        toast.error(response.message || 'Failed to load service locations');
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load service locations'));
    } finally {
      setLoading(false);
    }
  };

  const setupAddressAutocomplete = () => {
    if (!mapsInitialized || !addressAutocompleteRef.current) return;

    try {
      if (!window.google?.maps?.places?.Autocomplete) {
        console.error('Google Places Autocomplete is unavailable');
        return;
      }

      const autocompleteInstance = new window.google.maps.places.Autocomplete(
        addressAutocompleteRef.current,
        {
          types: ['establishment', 'geocode'], // Include business names
          componentRestrictions: { country: 'us' }
        }
      );

      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();
        if (place && place.geometry && place.geometry.location) {
          const location = place.formatted_address;
          let city = '';
          let state = '';
          let zipCode = '';

          if (place.address_components) {
            place.address_components.forEach(component => {
              const types = component.types;
              if (types.includes('locality')) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                state = component.short_name;
              } else if (types.includes('postal_code')) {
                zipCode = component.long_name;
              }
            });
          }

          setFormData(prev => ({
            ...prev,
            address: location,
            city,
            state,
            zip_code: zipCode,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          }));
        }
      });

      setAutocomplete(autocompleteInstance);
    } catch (error) {
      console.error('Error setting up address autocomplete:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    e.stopPropagation(); // Prevent event from bubbling to parent forms
    
    // Client-side validation for phone number
    if (!formData.phone_number || formData.phone_number.trim() === '') {
      toast.error('Phone number is required');
      return;
    }

    try {
      setSubmitting(true);
      
      // Clean the form data - convert empty strings to null for optional fields
      const cleanFormData = {
        ...formData,
        name: formData.name.trim() || DEFAULT_LOCATION_NAME,
        latitude: formData.latitude === '' || formData.latitude === null ? null : formData.latitude,
        longitude: formData.longitude === '' || formData.longitude === null ? null : formData.longitude,
        email_address: formData.email_address === '' ? null : formData.email_address,
        address_2: formData.address_2 === '' ? null : formData.address_2,
        city: formData.city === '' ? null : formData.city,
        state: formData.state === '' ? null : formData.state,
        zip_code: formData.zip_code === '' ? null : formData.zip_code
      };
      
      console.log('Creating service location with data:', cleanFormData);
      
      const response = await makeAuthenticatedRequest('/service-locations', {
        method: 'POST',
        body: JSON.stringify(cleanFormData)
      });

      console.log('Service location creation response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response));

      if (response.success) {
        toast.success('Service location created successfully');
        setShowAddModal(false);
        resetForm();
        loadLocations();
        // Auto-select the newly created location with a small delay to ensure state is updated
        setTimeout(() => {
          onLocationSelect(response.data);
        }, 100);
      } else {
        toast.error(
          response.message ||
            'Failed to create service location. Please check all required fields.'
        );
      }
    } catch (error) {
      console.error('Error creating service location:', error);
      toast.error(
        getCustomerPortalErrorMessage(
          error,
          'Failed to create service location. Please check your connection and try again.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      address_2: '',
      phone_number: '',
      email_address: '',
      city: '',
      state: '',
      zip_code: '',
      latitude: null,
      longitude: null
    });
    // Clear the autocomplete input field instead of setting place to null
    if (addressAutocompleteRef.current) {
      addressAutocompleteRef.current.value = '';
    }
  };

  const searchLower = (searchTerm || '').toLowerCase();
  const filteredLocations = locations.filter((location) => {
    const name = String(location.name ?? '').toLowerCase();
    const address = String(location.address ?? '').toLowerCase();
    const address2 = String(location.address_2 ?? '').toLowerCase();
    const city = String(location.city ?? '').toLowerCase();
    return (
      name.includes(searchLower) ||
      address.includes(searchLower) ||
      address2.includes(searchLower) ||
      city.includes(searchLower)
    );
  });

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Location Display */}
      <div
        className="w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        {selectedLocation ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MapPinIcon className="h-5 w-5 text-gray-400" />
              <div>
                <div className="font-medium text-gray-900">{selectedLocation.name}</div>
                <div className="text-sm text-gray-500">
                  {selectedLocation.address}
                  {selectedLocation.address_2 && (
                    <>
                      <br />
                      {selectedLocation.address_2}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="text-gray-400">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <MapPinIcon className="h-5 w-5 text-gray-400" />
            <span className="text-gray-500">{placeholder}</span>
          </div>
        )}
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search locations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Locations List */}
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading locations...</div>
              ) : filteredLocations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No locations found</div>
              ) : (
                filteredLocations.map((location) => (
                  <div
                    key={location.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => {
                      onLocationSelect(location);
                      setShowDropdown(false);
                      setSearchTerm('');
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900">{location.name}</div>
                        <div className="text-sm text-gray-500">
                          {location.address}
                          {location.address_2 && (
                            <>
                              <br />
                              {location.address_2}
                            </>
                          )}
                        </div>
                        {location.phone_number && (
                          <div className="text-xs text-gray-400 flex items-center mt-1">
                            <PhoneIcon className="h-3 w-3 mr-1" />
                            {location.phone_number}
                          </div>
                        )}
                        {location.email_address && (
                          <div className="text-xs text-gray-400 flex items-center mt-1">
                            <EnvelopeIcon className="h-3 w-3 mr-1" />
                            {location.email_address}
                          </div>
                        )}
                        {location.location_contact && (
                          <div className="text-xs text-gray-400 flex items-center mt-1">
                            <UserIcon className="h-3 w-3 mr-1" />
                            {location.location_contact}
                          </div>
                        )}
                      </div>
                      {selectedLocation && selectedLocation.id === location.id && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add New Location Button */}
            {showAddNew && (
              <div className="p-3 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowAddModal(true);
                    setShowDropdown(false);
                  }}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Add New Location</span>
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add New Location Modal - Rendered via Portal to prevent form nesting issues */}
      {showAddModal && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAddModal(false);
              resetForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add New Service Location</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Location Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={`Defaults to ${DEFAULT_LOCATION_NAME} if blank`}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={addressAutocompleteRef}
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder={mapsInitialized ? "Start typing address..." : "Enter address"}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {mapsInitialized ? (
                      <p className="text-xs text-green-600 mt-1">
                        ✅ Google Maps enabled - Start typing to see address suggestions
                      </p>
                    ) : (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Google Maps Loading - You can still type addresses manually
                      </p>
                    )}
                  </div>

                  <Input
                    label="Apartment Number (Optional)"
                    value={formData.address_2}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_2: e.target.value }))}
                    placeholder="Apt, Suite, Unit, etc."
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="City"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City"
                    />
                    <Input
                      label="State"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="State"
                    />
                  </div>
                  
                  <Input
                    label="ZIP Code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                    placeholder="ZIP Code"
                  />
                  
                  <Input
                    label="Phone Number"
                    value={formData.phone_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="(555) 123-4567"
                    required
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_address: e.target.value }))}
                    placeholder="location@example.com"
                  />

                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowAddModal(false);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1"
                    >
                      {submitting ? 'Creating...' : 'Create Location'}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default ServiceLocationSelector;
